import { useState, useEffect, useCallback } from 'react'
import { offlineQueue } from '../services/offlineQueue'

export interface UseOfflineSyncReturn {
  isOnline: boolean
  pendingCount: number
  isSyncing: boolean
  lastSyncTime: number | null
  syncNow: () => Promise<void>
  queueRequest: (url: string, method: 'POST' | 'PUT', body: unknown) => Promise<void>
}

export function useOfflineSync(): UseOfflineSyncReturn {
  const [isOnline, setIsOnline] = useState(() => {
    return typeof navigator !== 'undefined' ? navigator.onLine : true
  })
  const [pendingCount, setPendingCount] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null)

  useEffect(() => {
    // Initial pending count
    offlineQueue.getPendingCount().then(setPendingCount)

    // Subscribe to pending count changes
    const unsubscribe = offlineQueue.addPendingListener(setPendingCount)

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Listen for sync completion
    const handleSyncComplete = (success: boolean) => {
      setIsSyncing(false)
      if (success) {
        setLastSyncTime(Date.now())
      }
    }

    offlineQueue.onSyncComplete(handleSyncComplete)

    return () => {
      unsubscribe()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      offlineQueue.removeListener(handleSyncComplete)
    }
  }, [])

  const syncNow = useCallback(async () => {
    if (isOnline) {
      setIsSyncing(true)
      try {
        await offlineQueue.sync()
      } finally {
        setIsSyncing(false)
      }
    }
  }, [isOnline])

  const queueRequest = useCallback(
    async (url: string, method: 'POST' | 'PUT', body: unknown) => {
      await offlineQueue.enqueue(url, method, body)
      if (isOnline) {
        await syncNow()
      }
    },
    [isOnline, syncNow]
  )

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    syncNow,
    queueRequest
  }
}
